import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NumericInput } from '@/components/ui/numeric-input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, GripVertical, Phone, Mail, Info } from 'lucide-react';
import { toast } from 'sonner';
import type { StrategyAction } from '@/types/collection-strategies';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useActionTypeOptions, useTemplatesByType } from '@/hooks/useStrategiesData';

interface StrategyStageEditorProps {
  actions: StrategyAction[];
  onActionsChange: (actions: StrategyAction[]) => void;
}

const smsTemplates = [
  'SMS - Payment Due',
  'SMS - Urgent Payment',
  'SMS - Payment Link',
];

export function StrategyStageEditor({ actions, onActionsChange }: StrategyStageEditorProps) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [, setErrors] = useState<Record<string, string>>({});
  // setErrors is available for future validation implementation
  void setErrors;

  // Lookup data for dropdowns (cached for 1 minute)
  const { data: actionTypeOptions = [] } = useActionTypeOptions();
  const { data: emailTemplateOptions = [] } = useTemplatesByType('email');

  const addStage = () => {
    if (actions.length >= 10) {
      toast.error('Maximum 10 stages reached');
      return;
    }

    const newAction: StrategyAction = {
      id: `action-${Date.now()}`,
      type: 'email',
      timing: {
        type: 'days_from_trigger',
        daysFromTrigger: 0,
      },
      templateName: '',
      order: actions.length + 1,
    };

    onActionsChange([...actions, newAction]);
  };

  const removeStage = (index: number) => {
    const updatedActions = actions.filter((_, i) => i !== index);
    // Renumber actions
    const renumberedActions = updatedActions.map((action, i) => ({
      ...action,
      order: i + 1,
    }));
    onActionsChange(renumberedActions);
  };

  const updateStage = (index: number, field: keyof StrategyAction, value: StrategyAction[keyof StrategyAction] | Partial<StrategyAction['timing']>) => {
    const updatedActions = [...actions];
    
    // Handle nested timing object
    if (field === 'timing' && typeof value === 'object' && value !== null) {
      updatedActions[index] = { 
        ...updatedActions[index], 
        timing: { ...updatedActions[index].timing, ...(value as Partial<StrategyAction['timing']>) }
      };
    } else {
      updatedActions[index] = { ...updatedActions[index], [field]: value };
    }
    
    // Reset template/taskTitle when type changes
    if (field === 'type') {
      const newType = value as StrategyAction['type'];
      updatedActions[index].templateName = '';
      if (newType === 'phone_call') {
        updatedActions[index].taskTitle = 'Call customer re: overdue invoice';
      } else {
        updatedActions[index].taskTitle = '';
      }
    }
    
    onActionsChange(updatedActions);
  };

  const moveStage = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= actions.length) return;

    const updatedActions = [...actions];
    const [movedAction] = updatedActions.splice(fromIndex, 1);
    updatedActions.splice(toIndex, 0, movedAction);

    // Renumber actions
    const renumberedActions = updatedActions.map((action, i) => ({
      ...action,
      order: i + 1,
    }));

    onActionsChange(renumberedActions);
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    moveStage(draggedIndex, index);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const getTemplateOptions = (type: StrategyAction['type']) => {
    switch (type) {
      case 'email':
        return emailTemplateOptions.map((t) => t.name);
      case 'sms':
        return smsTemplates;
      default:
        return [];
    }
  };

  const showTemplateField = (type: StrategyAction['type']) => {
    return type === 'email' || type === 'sms';
  };

  const getActionTypeLabel = (type: StrategyAction['type']): string => {
    const labels: Record<StrategyAction['type'], string> = {
      email: 'Email',
      phone_call: 'Phone Call',
      payment_link: 'Payment Link',
      sms: 'SMS',
      escalation: 'Escalation',
      account_action: 'Place On Stop',
      payment_plan: 'Payment Plan',
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Collection Stages</h3>
          <p className="text-sm text-muted-foreground">
            Define collection actions with timelines and templates
          </p>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={addStage}
                disabled={actions.length >= 10}
                size="sm"
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Stage
              </Button>
            </TooltipTrigger>
            {actions.length >= 10 && (
              <TooltipContent>
                <p>Maximum 10 stages reached</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Stage Cards */}
      <div className="space-y-3">
        {actions.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-8">
              <p className="text-muted-foreground mb-4">No collection stages defined</p>
              <Button onClick={addStage} variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add First Stage
              </Button>
            </CardContent>
          </Card>
        ) : (
          actions
            .sort((a, b) => a.order - b.order)
            .map((action, index) => {
              const isPhoneCall = action.type === 'phone_call';
              return (
              <Card
                key={action.id}
                className={cn(
                  'border transition-colors',
                  isPhoneCall
                    ? 'border-orange-200 bg-orange-50/40 hover:border-orange-300 dark:bg-orange-950/10 dark:border-orange-800 dark:hover:border-orange-700'
                    : 'hover:border-teal-300 dark:hover:border-teal-700'
                )}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
              >
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    {/* Drag Handle and Stage Badge */}
                    <div className="flex items-center gap-2 flex-shrink-0 pt-2">
                      <div className="cursor-grab active:cursor-grabbing">
                        <GripVertical className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <Badge
                        className={cn(
                          'text-white whitespace-nowrap flex items-center gap-1',
                          isPhoneCall ? 'bg-orange-500 hover:bg-orange-600' : 'bg-teal-600 hover:bg-teal-700'
                        )}
                      >
                        {isPhoneCall ? (
                          <Phone className="h-3 w-3" />
                        ) : (
                          <Mail className="h-3 w-3" />
                        )}
                        Stage {action.order}
                      </Badge>
                    </div>

                    {/* All Fields in a Single Row */}
                    <div className="flex-1 grid grid-cols-4 gap-3">
                      {/* Action Type */}
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-1">
                          <Label htmlFor={`actionType-${index}`} className="text-xs">
                            Action Type <span className="text-red-500">*</span>
                          </Label>
                          {isPhoneCall && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-3.5 w-3.5 text-orange-500 cursor-help flex-shrink-0" />
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs text-xs">
                                  This task will be automatically assigned to the customer's designated Credit Controller. The collection strategy will continue to progress through subsequent stages regardless of whether this call is completed.
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        <Select
                          value={action.type}
                          onValueChange={(value: StrategyAction['type']) => updateStage(index, 'type', value)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {actionTypeOptions.length > 0 ? (
                              actionTypeOptions
                                .filter((option) => !['payment_link', 'sms', 'payment_plan'].includes(option.code))
                                .map((option) => (
                                  <SelectItem key={option.id} value={option.code}>
                                    {option.name}
                                  </SelectItem>
                                ))
                            ) : (
                              <>
                                <SelectItem value="email">Email</SelectItem>
                                <SelectItem value="phone_call">Phone Call</SelectItem>
                                {/* <SelectItem value="payment_link">Payment Link</SelectItem> */}
                                {/* <SelectItem value="sms">SMS</SelectItem> */}
                                <SelectItem value="escalation">Escalation</SelectItem>
                                <SelectItem value="account_action">Place On Stop</SelectItem>
                                {/* <SelectItem value="payment_plan">Payment Plan</SelectItem> */}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Timing Type */}
                      <div className="space-y-1.5">
                        <Label htmlFor={`timingType-${index}`} className="text-xs">
                          Timing Type <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={action.timing.type}
                                                    onValueChange={(value: 'days_from_trigger' | 'days_from_previous' | 'conditional') => {
                                                      const newTiming = {
                                                        type: value,
                                                        daysFromTrigger: value === 'days_from_trigger' ? (action.timing.daysFromTrigger || 0) : undefined,
                                                        daysFromPrevious: value === 'days_from_previous' ? (action.timing.daysFromPrevious ?? 0) : undefined,
                                                      };
                                                      updateStage(index, 'timing', newTiming);
                                                    }}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="days_from_trigger">Days from Trigger</SelectItem>
                            <SelectItem value="days_from_previous">Days from Previous</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Days Input (conditional based on timing type) */}
                      <div className="space-y-1.5">
                        {action.timing.type === 'days_from_trigger' && (
                          <>
                            <Label htmlFor={`daysFromTrigger-${index}`} className="text-xs">
                              Days from Trigger <span className="text-red-500">*</span>
                            </Label>
                            <NumericInput
                              id={`daysFromTrigger-${index}`}
                              min={0}
                              integer
                              sign="positive"
                              value={action.timing.daysFromTrigger ?? 0}
                              onChange={(value) => {
                                updateStage(index, 'timing', {
                                  ...action.timing,
                                  daysFromTrigger: value ?? 0,
                                });
                              }}
                              placeholder="Days"
                              className="h-9"
                            />
                          </>
                        )}
                        {action.timing.type === 'days_from_previous' && (
                          <>
                            <Label htmlFor={`daysFromPrevious-${index}`} className="text-xs">
                              Days from Previous <span className="text-red-500">*</span>
                            </Label>
                            <NumericInput
                              id={`daysFromPrevious-${index}`}
                              min={0}
                              integer
                              sign="positive"
                              value={action.timing.daysFromPrevious ?? 0}
                              onChange={(value) => {
                                updateStage(index, 'timing', {
                                  ...action.timing,
                                  daysFromPrevious: value ?? 0,
                                });
                              }}
                              placeholder="Days"
                              className="h-9"
                            />
                          </>
                        )}
                        {action.timing.type === 'conditional' && (
                          <>
                            <Label className="text-xs">Conditional</Label>
                            <div className="h-9 flex items-center px-3 border rounded-md bg-muted text-xs text-muted-foreground">
                              Based on conditions
                            </div>
                          </>
                        )}
                      </div>

                      {/* 4th field: Task Title (Phone Call) / Template (Email/SMS) / Action label */}
                      <div className="space-y-1.5">
                        {action.type === 'phone_call' && (
                          <>
                            <Label htmlFor={`taskTitle-${index}`} className="text-xs">
                              Task Title <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              id={`taskTitle-${index}`}
                              type="text"
                              value={
                                action.taskTitle ?? 'Call customer re: overdue invoice'
                              }
                              onChange={(e) => updateStage(index, 'taskTitle', e.target.value)}
                              placeholder="Call customer re: overdue invoice"
                              className="h-9"
                            />
                          </>
                        )}
                        {action.type !== 'phone_call' && showTemplateField(action.type) && (
                          <>
                            <Label htmlFor={`template-${index}`} className="text-xs">
                              Template <span className="text-red-500">*</span>
                            </Label>
                            <Select
                              value={action.templateName}
                              onValueChange={(value) => updateStage(index, 'templateName', value)}
                            >
                              <SelectTrigger className={cn('h-9', action.type === 'email' && !action.templateName?.trim() && 'border-red-500')}>
                                <SelectValue placeholder="Select template" />
                              </SelectTrigger>
                              <SelectContent>
                                {getTemplateOptions(action.type).map((template) => (
                                  <SelectItem key={template} value={template}>
                                    {template}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </>
                        )}
                        {action.type !== 'phone_call' && !showTemplateField(action.type) && (
                          <>
                            <Label className="text-xs">Action</Label>
                            <div className="h-9 flex items-center px-3 border rounded-md bg-muted text-xs">
                              {getActionTypeLabel(action.type)}
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Delete Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeStage(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 flex-shrink-0 h-9 w-9 p-0 mt-6"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
              );
            })
        )}
      </div>
    </div>
  );
}
